import {useEffect, useMemo, useState} from 'react';
import {
  Calculator,
  Eye,
  Heart,
  MapPin,
  MessageSquare,
  Pencil,
  Phone,
  Rotate3d,
  Scale,
  ShieldCheck,
} from 'lucide-react';
import {date, money, number} from '../lib/format';
import {Pano360} from '../pano/Pano360';
import {useAppLink} from '../shell/AppLink';
import {useStore} from '../store/StoreContext';
import {
  DIRECTION,
  FURNISHING,
  LEGAL,
  LISTING_STATUS,
  LISTING_TYPE,
  PURPOSE,
  pricePerM2,
  type Listing,
  type Photo,
} from '../store/types';
import {useMediaSrc} from '../lib/media';
import {Button, Card, Field, Pill, Segmented, TextArea, TextInput} from '../ui/primitives';
import {Picture} from './Picture';

/* ---------------------------------------------------------------- gallery */

function Stage({photo}: {photo: Photo | null}) {
  const src = useMediaSrc(photo?.ref ?? null, '360');

  if (!photo) {
    return <div className="frame rounded-card" style={{aspectRatio: '16 / 9'}} />;
  }

  if (photo.kind === '360') {
    return (
      <div className="relative w-full" style={{aspectRatio: '16 / 9'}}>
        {src ? (
          <Pano360 src={src} label={photo.label} />
        ) : (
          <div className="pano-stage flex h-full w-full items-center justify-center rounded-card text-[12px] font-semibold text-white/80">
            Đang dựng ảnh 360…
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <Picture alt={photo.label} className="rounded-card" ratio="16 / 9" refItem={photo.ref} />
      {photo.label ? (
        <span className="photo-tag absolute left-3 top-3 rounded-full px-3 py-1.5 text-[11px] font-semibold">
          {photo.label}
        </span>
      ) : null}
    </div>
  );
}

function Gallery({listing}: {listing: Listing}) {
  const flat = listing.photos.filter((photo) => photo.kind === 'anh');
  const tours = listing.photos.filter((photo) => photo.kind === '360');
  const [mode, setMode] = useState<'anh' | '360'>(flat.length ? 'anh' : '360');
  const list = mode === '360' ? tours : flat;
  const [index, setIndex] = useState(0);
  const active = list[Math.min(index, list.length - 1)] ?? null;

  return (
    <section>
      {tours.length > 0 && flat.length > 0 ? (
        <div className="mb-3">
          <Segmented
            value={mode}
            onChange={(next) => {
              setMode(next);
              setIndex(0);
            }}
            options={[
              {label: `Ảnh (${flat.length})`, value: 'anh'},
              {label: `Xem 360° (${tours.length})`, value: '360'},
            ]}
          />
        </div>
      ) : null}

      <Stage photo={active} />

      {list.length > 1 ? (
        <ul className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {list.map((photo, position) => (
            <li key={photo.id} className="shrink-0">
              <button
                type="button"
                onClick={() => setIndex(position)}
                aria-label={photo.label}
                className={`block w-24 overflow-hidden rounded-control ${
                  position === index ? 'ring-2 ring-[var(--color-accent)]' : 'opacity-80'
                }`}
              >
                <Picture alt={photo.label} ratio="4 / 3" refItem={photo.ref} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/* ------------------------------------------------------------------ specs */

function Spec({label, value}: {label: string; value: string}) {
  return (
    <div className="tile rounded-control px-3 py-2.5">
      <dt className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-3">{label}</dt>
      <dd className="mt-1 text-[13px] font-semibold text-ink">{value}</dd>
    </div>
  );
}

/* ----------------------------------------------------------------- detail */

/**
 * The listing page.
 *
 * Order is deliberate: pictures, then price and address, then the numbers that
 * decide a shortlist, then the seller. The two things a visitor is most likely
 * to want next — the loan on THIS price, and a message to THIS seller — are
 * actions on the page rather than places they have to find.
 */
export function ListingDetail({listing}: {listing: Listing}) {
  const {compare, countView, profile, profileOf, saved, sendInquiry, toggleCompare, toggleSaved, user} =
    useStore();
  const {open} = useAppLink();
  const [showPhone, setShowPhone] = useState(false);
  const [contacting, setContacting] = useState(false);
  const [form, setForm] = useState({message: '', name: '', phone: '', visitAt: ''});
  const [sent, setSent] = useState(false);

  const owner = profileOf(listing.ownerId);
  const mine = user?.id === listing.ownerId;
  const rent = listing.purpose === 'cho-thue';
  const inCompare = compare.includes(listing.id);
  const isSaved = saved.includes(listing.id);

  useEffect(() => {
    countView(listing.id);
  }, [countView, listing.id]);

  useEffect(() => {
    // Re-prefill when the visitor signs in while the panel is open.
    setForm((prev) => ({
      ...prev,
      name: prev.name || profile?.name || '',
      phone: prev.phone || profile?.phone || '',
    }));
  }, [profile]);

  const specs = useMemo(
    () => [
      {label: 'Diện tích', value: `${number(listing.area)} m²`},
      {label: 'Giá / m²', value: `${money(pricePerM2(listing))}`},
      ...(listing.bedrooms > 0 ? [{label: 'Phòng ngủ', value: `${listing.bedrooms}`}] : []),
      ...(listing.bathrooms > 0 ? [{label: 'Phòng tắm', value: `${listing.bathrooms}`}] : []),
      ...(listing.floors > 1 ? [{label: 'Số tầng', value: `${listing.floors}`}] : []),
      {label: 'Hướng', value: DIRECTION[listing.direction]},
      {label: 'Pháp lý', value: LEGAL[listing.legal]},
      {label: 'Nội thất', value: FURNISHING[listing.furnishing]},
    ],
    [listing],
  );

  const canSend = form.name.trim().length > 1 && form.phone.trim().length >= 8;

  return (
    <div className="space-y-4">
      <Gallery listing={listing} />

      <header>
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone={rent ? 'sky' : 'emerald'}>{PURPOSE[listing.purpose]}</Pill>
          <Pill tone={LISTING_STATUS[listing.status].tone}>
            {LISTING_STATUS[listing.status].label}
          </Pill>
          {listing.verified ? (
            <Pill tone="violet">
              <ShieldCheck size={12} className="mr-1" /> Đã xác minh
            </Pill>
          ) : null}
          <span className="ml-auto flex items-center gap-1.5 text-[11.5px] text-ink-3">
            <Eye size={13} /> {number(listing.views)} lượt xem · {date(listing.createdAt)}
          </span>
        </div>

        <h1 className="mt-3 text-[19px] font-extrabold leading-tight text-ink @xl:text-[22px]">
          {listing.title}
        </h1>
        <p className="mt-2 flex items-start gap-1.5 text-[13px] text-ink-2">
          <MapPin size={15} className="mt-0.5 shrink-0 text-ink-3" />
          <span>
            {listing.address} · {listing.district}, {listing.province}
          </span>
        </p>
        <p className="mt-3 text-[26px] font-extrabold leading-none text-accent">
          {money(listing.price)}
          {rent ? <span className="text-[14px] font-bold text-ink-3"> /tháng</span> : null}
        </p>
        <p className="mt-1.5 text-[12px] text-ink-3">
          {LISTING_TYPE[listing.type]} · mã tin {listing.code}
        </p>
      </header>

      <dl className="grid grid-cols-2 gap-2 @xl:grid-cols-4">
        {specs.map((spec) => (
          <Spec key={spec.label} label={spec.label} value={spec.value} />
        ))}
      </dl>

      <div className="flex flex-wrap gap-2">
        <Button
          icon={<Heart size={15} fill={isSaved ? 'currentColor' : 'none'} />}
          onClick={() => toggleSaved(listing.id)}
        >
          {isSaved ? 'Đã lưu' : 'Lưu tin'}
        </Button>
        <Button icon={<Scale size={15} />} onClick={() => toggleCompare(listing.id)}>
          {inCompare ? 'Bỏ khỏi so sánh' : 'So sánh'}
        </Button>
        <Button
          icon={<Calculator size={15} />}
          onClick={() => open('loan', {label: listing.title, price: listing.price})}
        >
          Tính vay cho căn này
        </Button>
        {mine ? (
          <Button
            icon={<Pencil size={15} />}
            onClick={() => open('manage', {listingId: listing.id, tab: 'tin'})}
          >
            Sửa tin của tôi
          </Button>
        ) : null}
      </div>

      <Card title="Mô tả">
        <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-ink-2">
          {listing.description || 'Người đăng chưa viết mô tả cho tin này.'}
        </p>
      </Card>

      {listing.amenities.length > 0 ? (
        <Card title="Tiện ích">
          <ul className="flex flex-wrap gap-2">
            {listing.amenities.map((item) => (
              <li key={item}>
                <Pill tone="slate">{item}</Pill>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card title="Người đăng tin">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent text-[15px] font-bold text-accent-ink">
            {(owner?.name ?? listing.contactName ?? '?').slice(0, 1)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[14px] font-bold text-ink">
              {owner?.name || listing.contactName || 'Chủ nhà'}
            </p>
            <p className="text-[11.5px] text-ink-3">
              {owner?.verified ? 'Tài khoản đã xác minh' : 'Tài khoản chưa xác minh'}
              {owner ? ` · tham gia ${date(owner.createdAt)}` : ''}
            </p>
          </div>

          {mine ? null : showPhone ? (
            <a
              href={`tel:${listing.contactPhone.replace(/\s/g, '')}`}
              className="btn btn-primary inline-flex min-h-11 items-center gap-2 rounded-control px-4 text-[13px] font-semibold @xl:min-h-9"
            >
              <Phone size={15} /> {listing.contactPhone}
            </a>
          ) : (
            <Button
              tone="primary"
              icon={<Phone size={15} />}
              onClick={() => {
                // Signed-out visitors see the number too, but the account app is
                // one tap away — a wall here just sends people to a competitor.
                setShowPhone(true);
              }}
            >
              Hiện số điện thoại
            </Button>
          )}
        </div>

        {mine ? (
          <p className="mt-3 text-[12.5px] text-ink-3">
            Đây là tin của bạn. Mọi liên hệ từ người mua nằm trong app Quản lý.
          </p>
        ) : sent ? (
          <p className="mt-4 text-[13px] font-semibold text-accent">
            Đã gửi liên hệ. Người đăng sẽ thấy tin nhắn của bạn trong app Quản lý.
          </p>
        ) : contacting ? (
          <form
            className="mt-4 space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!canSend) return;
              void sendInquiry({
                fromId: user?.id ?? null,
                listingId: listing.id,
                message: form.message,
                name: form.name,
                phone: form.phone,
                visitAt: form.visitAt,
              });
              setSent(true);
              setContacting(false);
            }}
          >
            <div className="grid gap-3 @lg:grid-cols-2">
              <Field label="Tên của bạn">
                <TextInput value={form.name} onChange={(name) => setForm({...form, name})} />
              </Field>
              <Field label="Số điện thoại">
                <TextInput
                  type="tel"
                  value={form.phone}
                  onChange={(phone) => setForm({...form, phone})}
                />
              </Field>
              <Field label="Muốn xem nhà lúc" hint="Không bắt buộc">
                <TextInput
                  type="datetime-local"
                  value={form.visitAt}
                  onChange={(visitAt) => setForm({...form, visitAt})}
                />
              </Field>
            </div>
            <Field label="Lời nhắn">
              <TextArea
                value={form.message}
                placeholder="Căn này còn không anh/chị? Em muốn xem nhà cuối tuần…"
                onChange={(value) => setForm({...form, message: value})}
              />
            </Field>
            <div className="flex gap-2">
              <Button tone="primary" type="submit" disabled={!canSend}>
                Gửi liên hệ
              </Button>
              <Button onClick={() => setContacting(false)}>Huỷ</Button>
            </div>
          </form>
        ) : (
          <div className="mt-4">
            <Button
              icon={<MessageSquare size={15} />}
              onClick={() => setContacting(true)}
              wide
            >
              Gửi liên hệ / đặt lịch xem nhà
            </Button>
          </div>
        )}
      </Card>

      {listing.photos.some((photo) => photo.kind === '360') ? null : (
        <p className="flex items-center gap-2 text-[12px] text-ink-3">
          <Rotate3d size={14} /> Tin này chưa có ảnh 360°.
        </p>
      )}
    </div>
  );
}
